import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import MainCard from '@components/ui/cards/MainCard';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';

import MainTable from '@components/MainTable';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import { MultiDocumentAgentTableMeta } from '@app/types/overrides/table-meta';
import {
  MultiDocumentAgentTemplate,
  MultiDocumentAgentTemplateRow,
} from '@app/types/admin';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import { urls } from '@api/urls.ts';
import useToast from '@hooks/useToast';
import useConfirmationModal from '@hooks/useConfirmationModal';
import DuplicateTemplateModal from '@features/admin-multi-document-agent/components/DuplicateTemplateModal';
import { buildTemplateActionsMap } from '@features/admin-multi-document-agent/utils';
import { useMenuItemsSelector } from '@app/shared/hooks/useMenuItems';
import MultiDocumentAgentTableToolbar from './toolbar';
import createMultiDocumentAgentTemplateColumns from './columns';

interface UseCaseGroup {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function MultiDocumentAgentTable() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { showDeleteConfirmation, closeModal } = useConfirmationModal();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<MultiDocumentAgentTemplate | null>(null);
  const actionsMap = useMemo(
    () => buildTemplateActionsMap(navigate),
    [navigate]
  );
  const revalidate = useMenuItemsSelector((state) => state.revalidate);

  const { data, mutate, isLoading } = useDataFetcher<
    MultiDocumentAgentTemplateRow[]
  >({
    url: urls.allDocumentTemplates.base,
  });

  const { data: useCaseGroups } = useDataFetcher<UseCaseGroup[]>({
    url: urls.useCaseGroup.base,
  });

  const handleDelete = async (document: MultiDocumentAgentTemplate) => {
    try {
      await actionsMap[document.template_type].onDelete(document.id);
      showSuccess('Success', 'Your document has been deleted successfully');
      closeModal();
      mutate();
      revalidate();
    } catch (error) {
      showError('Error', 'There was an error deleting the document');
    }
  };

  const handleOpenDuplicateModal = (template: MultiDocumentAgentTemplate) => {
    setSelectedTemplate(template);
    setIsDuplicateModalOpen(true);
  };

  const handleCloseDuplicateModal = () => {
    setIsDuplicateModalOpen(false);
    setSelectedTemplate(null);
  };

  const { table, setData } = useTanStackTable<MultiDocumentAgentTemplateRow>({
    tableData: [],
    columnConfig: createMultiDocumentAgentTemplateColumns(mutate, navigate),
    options: {
      getRowId: (r) => `${r.template_type}-${r.id}`,
      meta: {
        handleDeleteRow: async (template: MultiDocumentAgentTemplate) => {
          showDeleteConfirmation(
            () => handleDelete(template),
            `Are you sure you want to delete document ${template.name}? This action cannot be undone.`
          );
        },
        handleEditRow: (template: MultiDocumentAgentTemplate) => {
          actionsMap[template.template_type].onEdit(template.id);
        },
        handleMoveUp: async (template: MultiDocumentAgentTemplate) => {
          await actionsMap[template.template_type].onMoveUp(template.id);
          mutate();
          revalidate();
        },
        handleMoveDown: async (template: MultiDocumentAgentTemplate) => {
          await actionsMap[template.template_type].onMoveDown(template.id);
          mutate();
          revalidate();
        },
        handleDuplicateRow: async (template: MultiDocumentAgentTemplate) => {
          handleOpenDuplicateModal(template);
        },
      } as MultiDocumentAgentTableMeta<MultiDocumentAgentTemplateRow>,
    },
  });

  useEffect(() => {
    if (!data) return;

    // Map use_case_group IDs to group names
    const groupMap = new Map(
      useCaseGroups?.map((group) => [group.id, group.name]) || []
    );

    const dataWithGroupNames = data.map((row) => ({
      ...row,
      use_case_group_name: row.use_case_group
        ? groupMap.get(row.use_case_group)
        : undefined,
    }));

    setData(dataWithGroupNames);
  }, [data, useCaseGroups, setData]);

  return (
    <MainCard>
      <Stack gap={2}>
        <MultiDocumentAgentTableToolbar />

        <Box>
          <MainTable table={table} isLoading={isLoading} />
          <MainTablePagination table={table} isLoading={isLoading} />
        </Box>
      </Stack>

      {isDuplicateModalOpen && selectedTemplate && (
        <DuplicateTemplateModal
          open={isDuplicateModalOpen}
          onClose={handleCloseDuplicateModal}
          template={selectedTemplate}
          onSuccess={() => {
            mutate();
            revalidate();
            handleCloseDuplicateModal();
          }}
        />
      )}
    </MainCard>
  );
}

export default MultiDocumentAgentTable;
